import React, { useState, useEffect, useCallback } from "react";
import { Users, UserPlus, Mail, Trash2, Shield, AlertCircle, Loader2, Search, Plus } from "lucide-react";
import { fetchTeamMembers, inviteTeamMember, removeTeamMember } from "../api";
import { getTeamLimit } from "../utils/subscriptionHelpers";

const TeamsManager = ({ userEmail, subscription, onUpgradePlan, initialMembers, isPreloaded }) => {
    const [members, setMembers] = useState(initialMembers || []);
    const [isLoading, setIsLoading] = useState(!isPreloaded);
    const [error, setError] = useState("");
    const [inviteEmail, setInviteEmail] = useState("");
    const [isInviting, setIsInviting] = useState(false);
    const [inviteError, setInviteError] = useState("");

    // Delete Confirmation State
    const [memberToDelete, setMemberToDelete] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const tier = subscription?.plan?.name || subscription?.tier || "Free";
    const limit = getTeamLimit(tier);
    const memberCount = members.length;
    const canInvite = memberCount < limit;

    const loadTeam = useCallback(async () => {
        setIsLoading(true);
        setError("");
        try {
            const response = await fetchTeamMembers();
            const data = response?.data?.data || response?.data || {};
            setMembers(Array.isArray(data.members) ? data.members : []);
        } catch (err) {
            console.error("Failed to load team:", err);
            setError("Unable to load team members.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isPreloaded) {
            loadTeam();
        }
    }, [isPreloaded, loadTeam]);

    const handleInvite = async (e) => {
        e.preventDefault();
        const emailToInvite = inviteEmail.trim();
        if (!emailToInvite) return;

        // Frontend Validation
        if (emailToInvite.toLowerCase() === userEmail?.toLowerCase()) {
            setInviteError("You cannot invite yourself to your own team.");
            return;
        }

        if (members.some(m => m.email.toLowerCase() === emailToInvite.toLowerCase())) {
            setInviteError("This user is already in your team or pending.");
            return;
        }

        if (!canInvite) {
            setInviteError(`Limit reached for your ${tier} plan.`);
            return;
        }

        setIsInviting(true);
        setInviteError("");
        try {
            const response = await inviteTeamMember(emailToInvite);
            const newMember = response?.data?.data?.member || response?.data?.member || {
                id: `temp-${Date.now()}`,
                name: emailToInvite.split('@')[0],
                email: emailToInvite,
                status: 'Pending',
                role: 'Member'
            };
            setMembers(prev => [...prev, newMember]);
            setInviteEmail("");
        } catch (err) {
            setInviteError(err?.message || "Failed to invite member.");
        } finally {
            setIsInviting(false);
        }
    };

    const handleRemoveClick = (member) => {
        setMemberToDelete(member);
        setIsDeleteModalOpen(true);
    };

    const confirmRemoveMember = async () => {
        if (!memberToDelete) return;
        try {
            await removeTeamMember(memberToDelete.id);
            setMembers(prev => prev.filter(m => m.id !== memberToDelete.id));
            setIsDeleteModalOpen(false);
            setMemberToDelete(null);
        } catch (err) {
            setError("Failed to remove member.");
            // Keep modal open or show error? For now, we set error state which is displayed elsewhere
            setIsDeleteModalOpen(false);
        }
    };

    return (
        <div className="flex flex-col h-full bg-[#f7f4ee] dark:bg-slate-950">
            {/* Hero */}
            <div className="relative overflow-hidden px-6 py-10 sm:py-12">
                <div
                    className="absolute inset-0"
                    style={{
                        background: 'linear-gradient(120deg, rgba(13, 148, 136, 0.98), rgba(15, 23, 42, 0.95))'
                    }}
                />
                <div
                    className="absolute inset-0 opacity-70"
                    style={{
                        backgroundImage: 'radial-gradient(circle at 15% 15%, rgba(255,255,255,0.3), transparent 40%), radial-gradient(circle at 80% 10%, rgba(255,255,255,0.2), transparent 35%)'
                    }}
                />
                <div className="relative max-w-5xl mx-auto">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                        <div className="text-white">
                            <p className="text-sm uppercase tracking-[0.2em] text-white/70">Team workspace</p>
                            <h2 className="text-3xl sm:text-4xl font-semibold mt-2">Build for High Performance Team</h2>
                            <p className="text-sm sm:text-base text-white/80 mt-2 max-w-xl">
                                Invite teammates, set roles, and collaborate on cases together.
                            </p>
                        </div>
                        <div className="w-full lg:w-[360px]">
                            <label className="text-xs font-semibold text-white/80 uppercase tracking-[0.2em]">
                                Search members
                            </label>
                            <div className="relative mt-3">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search team members..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3 bg-white/95 dark:bg-slate-800 border-0 rounded-xl shadow-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-teal-200"
                                />
                            </div>
                            <div className="flex flex-wrap gap-2 mt-4 text-xs text-white/80">
                                <span className="px-3 py-1.5 rounded-full bg-white/15 text-xs font-medium">
                                    {memberCount} / {limit} seats
                                </span>
                                <span className="px-3 py-1.5 rounded-full bg-white/15 text-xs font-medium">
                                    {tier} plan
                                </span>
                            </div>
                            <button
                                onClick={() => document.getElementById('team-invite-input')?.focus()}
                                disabled={!canInvite}
                                className="mt-4 w-full px-4 py-2.5 bg-white text-slate-900 hover:bg-white/90 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Plus className="w-5 h-5" />
                                Invite Member
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-auto px-6 pb-6 -mt-6 relative z-10">
                <div className="space-y-6">
                    <div className="dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6 shadow-sm">
                        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
                            <div className="flex-1">
                                <h3 className="text-xl font-bold dark:text-white text-slate-900 mb-1">Team Management</h3>
                                <p className="text-sm dark:text-slate-400 text-slate-500">
                                    Invite, manage, and collaborate with your team members.
                                </p>
                            </div>
                            <div className="flex items-center gap-2 text-sm px-3 py-1.5 dark:bg-slate-800 bg-slate-100 border dark:border-transparent border-slate-200 rounded-lg w-fit">
                                <Shield className="w-4 h-4 text-teal-500" />
                                <span className="dark:text-slate-300 text-slate-600">Plan: <span className="font-semibold dark:text-white text-slate-900 capitalize">{tier}</span></span>
                                <span className="text-slate-500">•</span>
                                <span className={`${memberCount >= limit ? 'text-amber-400 dark:text-amber-400' : 'dark:text-slate-300 text-slate-600'}`}>
                                    {memberCount} / {limit} seats used
                                </span>
                            </div>
                        </div>

                        {canInvite ? (
                            <form onSubmit={handleInvite} className="flex gap-2 w-full md:w-auto">
                                <div className="relative flex-1 md:w-64">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                    <input
                                        type="email"
                                        placeholder="colleague@example.com"
                                        value={inviteEmail}
                                        onChange={(e) => setInviteEmail(e.target.value)}
                                        id="team-invite-input"
                                        className="w-full pl-9 pr-4 py-2 dark:bg-slate-950 bg-slate-50 border dark:border-slate-700 border-slate-300 rounded-lg text-sm dark:text-white text-slate-900 focus:ring-1 focus:ring-teal-500 outline-none"
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={isInviting || !inviteEmail}
                                    className="px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {isInviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                                    Invite Member
                                </button>
                            </form>
                        ) : (
                            <button onClick={onUpgradePlan} className="px-4 py-2 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20 rounded-lg text-sm font-medium transition-colors">
                                Upgrade to add more members
                            </button>
                        )}
                    </div>

                    {inviteError && (
                        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-sm text-red-400">
                            <AlertCircle className="w-4 h-4" />
                            {inviteError}
                        </div>
                    )}

                    {isLoading ? (
                        <div className="overflow-hidden rounded-lg border border-slate-800 animate-pulse bg-slate-900/50">
                            <div className="border-b border-slate-800 bg-slate-950 p-4">
                                <div className="flex justify-between gap-4">
                                    <div className="h-4 w-24 bg-slate-800 rounded"></div>
                                    <div className="h-4 w-32 bg-slate-800 rounded"></div>
                                    <div className="h-4 w-16 bg-slate-800 rounded"></div>
                                    <div className="h-4 w-16 bg-slate-800 rounded"></div>
                                </div>
                            </div>
                            {[1, 2, 3, 4, 5].map((i) => (
                                <div key={i} className="flex justify-between items-center p-4 border-b border-slate-800/50">
                                    <div className="h-4 w-32 bg-slate-800 rounded"></div>
                                    <div className="h-4 w-48 bg-slate-800 rounded"></div>
                                    <div className="h-6 w-20 bg-slate-800 rounded-full"></div>
                                    <div className="h-8 w-8 bg-slate-800 rounded"></div>
                                </div>
                            ))}
                        </div>
                    ) : members.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                            <Users className="w-12 h-12 mx-auto mb-3 opacity-20" />
                            <p>No team members yet.</p>
                        </div>
                    ) : (
                        <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800">
                            <table className="w-full text-left text-sm">
                                <thead className="dark:bg-slate-900 bg-slate-50 dark:text-slate-400 text-slate-500 border-b dark:border-slate-800 border-slate-200">
                                    <tr>
                                        <th className="px-4 py-3 font-semibold">Name</th>
                                        <th className="px-4 py-3 font-semibold">Email</th>
                                        <th className="px-4 py-3 font-semibold">Status</th>
                                        <th className="px-4 py-3 font-semibold text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y dark:divide-slate-800 divide-slate-100 dark:bg-slate-900/50 bg-white">
                                    {members.map(member => (
                                        <tr key={member.id} className="group hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                            <td className="px-4 py-3 font-medium dark:text-white text-slate-900">{member.name || 'Unknown'}</td>
                                            <td className="px-4 py-3 dark:text-slate-400 text-slate-500">{member.email}</td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${member.status === 'Active' ? 'dark:bg-green-500/10 dark:text-green-400 bg-green-50 text-green-700 border border-green-200 dark:border-transparent' : 'dark:bg-amber-500/10 dark:text-amber-400 bg-amber-50 text-amber-700 border border-amber-200 dark:border-transparent'
                                                    }`}>
                                                    {member.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <button
                                                    onClick={() => handleRemoveClick(member)}
                                                    className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
                                                    title="Remove member"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
                    <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-sm shadow-2xl p-6">
                        <div className="flex flex-col items-center text-center space-y-4">
                            <div className="w-12 h-12 bg-red-500/10 text-red-400 rounded-full flex items-center justify-center">
                                <Trash2 className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-white">Remove Team Member?</h3>
                                <p className="text-sm text-slate-400 mt-2">
                                    Are you sure you want to remove <span className="font-semibold text-white">{memberToDelete?.email}</span>?
                                    They will lose access to all shared cases.
                                </p>
                            </div>
                            <div className="flex gap-3 w-full mt-2">
                                <button
                                    onClick={() => setIsDeleteModalOpen(false)}
                                    className="flex-1 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-sm font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmRemoveMember}
                                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-medium transition-colors"
                                >
                                    Remove
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeamsManager;
